import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import UpgradeModal from '../components/UpgradeModal';
import SignInModal from '../components/SignInModal';
import { Search, Filter, FileText, BookOpen, Video, TestTube, Lightbulb, PenTool, AlertCircle, Lock } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BrowseResources() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedPremium, setSelectedPremium] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [user, setUser] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState(null);

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'worksheet', label: 'Worksheets', icon: FileText },
    { value: 'test_paper', label: 'Test Papers', icon: TestTube },
    { value: 'lesson_plan', label: 'Lesson Plans', icon: BookOpen },
    { value: 'teaching_notes', label: 'Teaching Notes', icon: PenTool },
    { value: 'reading_resource', label: 'Reading Resources', icon: Video },
    { value: 'quiz', label: 'Quizzes', icon: Lightbulb }
  ];

  const grades = [
    { value: '', label: 'All Years' },
    { value: 'Year 2', label: 'Year 2' },
    { value: 'Year 3', label: 'Year 3' },
    { value: 'Year 4', label: 'Year 4' },
    { value: 'Year 5', label: 'Year 5' },
    { value: 'Year 6', label: 'Year 6' },
    { value: 'Other', label: 'Other' }
  ];

  const subjects = [
    { value: '', label: 'All Subjects' },
    { value: 'Math', label: 'Math' },
    { value: 'Science', label: 'Science' },
    { value: 'English', label: 'English' },
    { value: 'Social Studies', label: 'Social Studies' },
    { value: 'Reading', label: 'Reading' },
    { value: 'Writing', label: 'Writing' }
  ];

  const premiumOptions = [
    { value: '', label: 'All Resources' },
    { value: 'free', label: 'Free Only' },
    { value: 'premium', label: 'Premium Only' }
  ];

  const checkUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  }, []);

  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedGrade) params.append('grade_level', selectedGrade);
      if (selectedSubject) params.append('subject', selectedSubject);
      if (selectedPremium === 'free') params.append('is_premium', 'false');
      if (selectedPremium === 'premium') params.append('is_premium', 'true');
      if (selectedTopic) params.append('chapter_topic', selectedTopic);
      
      const response = await axios.get(`${API}/resources?${params.toString()}`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedGrade, selectedSubject, selectedPremium, selectedTopic]);

  useEffect(() => {
    fetchResources();
    checkUser();
  }, [fetchResources, checkUser]);

  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.icon || FileText;
  };

  const handleResourceClick = (e, resource) => {
    // ALWAYS require sign-in for ALL resources
    if (!user) {
      e.preventDefault();
      setSelectedResourceId(resource.resource_id);
      setShowSignInModal(true);
      return;
    }
    
    // If signed in, check premium access for premium resources
    if (resource.is_premium && user.subscription_tier === 'free') {
      e.preventDefault();
      setShowUpgradeModal(true);
      return;
    }
    
    // Allow access for signed-in users with appropriate tier
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold text-slate-900 mb-4" data-testid="browse-title">
              Browse Resources
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              Classroom-ready resources designed by a teacher for teachers
            </p>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {/* Quick Filter Buttons */}
            <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b border-slate-200">
              <button
                onClick={() => setSelectedPremium('')}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all ${
                  selectedPremium === '' 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                All Resources
              </button>
              <button
                onClick={() => setSelectedPremium('free')}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  selectedPremium === 'free' 
                    ? 'bg-green-600 text-white shadow-md' 
                    : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200'
                }`}
              >
                <span className="text-lg">🎁</span>
                Free Resources
              </button>
              <button
                onClick={() => setSelectedPremium('premium')}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  selectedPremium === 'premium' 
                    ? 'bg-purple-600 text-white shadow-md' 
                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-2 border-purple-200'
                }`}
              >
                <Lock className="w-4 h-4" />
                Premium Only
              </button>
            </div>
            
            {/* BUILD/GROW/SHINE Explanation */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-slate-700 mb-3">📊 Differentiation Levels:</p>
              <div className="grid sm:grid-cols-3 gap-3 text-xs sm:text-sm">
                <div className="flex items-start gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-green-500 flex-shrink-0 mt-1"></span>
                  <div>
                    <span className="font-bold text-slate-900">BUILD</span>
                    <span className="text-slate-600"> — More support & scaffolding</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0 mt-1"></span>
                  <div>
                    <span className="font-bold text-slate-900">GROW</span>
                    <span className="text-slate-600"> — Independent practice</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-red-500 flex-shrink-0 mt-1"></span>
                  <div>
                    <span className="font-bold text-slate-900">SHINE</span>
                    <span className="text-slate-600"> — Challenge & deeper thinking</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="relative md:col-span-3">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full rounded-xl border-2 border-slate-200 bg-white pl-12 pr-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
              </div>
              
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                data-testid="category-filter"
                className="rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                data-testid="grade-filter"
                className="rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              >
                {grades.map(grade => (
                  <option key={grade.value} value={grade.value}>{grade.label}</option>
                ))}
              </select>
              
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                data-testid="subject-filter"
                className="rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              >
                {subjects.map(subject => (
                  <option key={subject.value} value={subject.value}>{subject.label}</option>
                ))}
              </select>
            </div>
            
            <div className="grid md:grid-cols-1 gap-4">
              <input
                type="text"
                placeholder="Filter by topic/chapter..."
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                data-testid="topic-filter"
                className="rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              />
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
              <p className="text-lg text-slate-600">Loading resources...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-slate-400" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3">
                No resources found
              </h3>
              <p className="text-lg text-slate-600 mb-8">
                Try adjusting your filters or search query
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource, index) => {
                const Icon = getCategoryIcon(resource.category);
                const isPremium = resource.is_premium;
                
                return (
                  <Link
                    key={resource.resource_id}
                    to={`/resources/${resource.resource_id}`}
                    onClick={(e) => handleResourceClick(e, resource)}
                    data-testid={`resource-card-${index}`}
                    className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col relative"
                  >
                    {isPremium && user?.subscription_tier === 'free' && (
                      <div className="absolute inset-0 bg-slate-900/5 backdrop-blur-[2px] rounded-2xl flex items-center justify-center">
                        <div className="bg-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                          <Lock className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-semibold text-slate-900">Upgrade to Access</span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-blue-50 rounded-xl p-2.5 inline-block">
                        <Icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                      </div>
                      {isPremium ? (
                        <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Premium
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-lg">
                          Free
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
                      {resource.title}
                    </h3>
                    
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4 flex-grow">
                      {resource.description}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-2 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                        <span className="text-xs text-slate-600 font-medium">{resource.subject || 'General'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-600 font-medium">{resource.grade_level}</span>
                      </div>
                      {resource.publisher && (
                        <div className="flex items-center gap-1.5 col-span-2">
                          <span className="text-xs text-slate-600">{resource.publisher}</span>
                        </div>
                      )}
                      {resource.chapter_topic && (
                        <div className="flex items-center gap-1.5 col-span-2">
                          <span className="text-xs text-slate-600">{resource.chapter_topic}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
      
      <SignInModal 
        isOpen={showSignInModal} 
        onClose={() => setShowSignInModal(false)}
        redirectUrl={selectedResourceId ? `/resources/${selectedResourceId}` : '/browse'}
      />
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      
      <Footer />
    </div>
  );
}

export default BrowseResources;
